import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CheckCircle2, X } from "lucide-react";
import { Link } from "react-router-dom";

interface SuccessAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface SuccessActionPanelProps {
  title: string;
  description: string;
  details?: string[];
  nextAction?: string;
  actions?: SuccessAction[];
  onDismiss?: () => void;
  onNavigate?: () => void;
}

export function SuccessActionPanel(props: Readonly<SuccessActionPanelProps>) {
  const {
    title,
    description,
    details = [],
    nextAction,
    actions = [],
    onDismiss,
    onNavigate,
  } = props;

  return (
    <Card className="border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/30">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-emerald-100 p-2 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
              <CheckCircle2 className="h-4 w-4" />
            </div>
            <div className="space-y-1">
              <CardTitle className="text-base">{title}</CardTitle>
              <CardDescription className="text-emerald-900/80 dark:text-emerald-100/80">
                {description}
              </CardDescription>
            </div>
          </div>
          {onDismiss ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDismiss}
              className="size-8"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {details.length > 0 ? (
          <div className="space-y-1">
            {details.map((detail) => (
              <div
                key={detail}
                className="text-sm text-emerald-950/80 dark:text-emerald-100/80"
              >
                {detail}
              </div>
            ))}
          </div>
        ) : null}
        {nextAction ? (
          <div className="text-sm font-medium text-emerald-950 dark:text-emerald-50">
            Recommended next action: {nextAction}
          </div>
        ) : null}
        {actions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {actions.map((action) =>
              action.href ? (
                <Button asChild key={action.label} size="sm">
                  <Link
                    to={action.href}
                    onClick={() => {
                      action.onClick?.();
                      onNavigate?.();
                    }}
                  >
                    {action.label}
                  </Link>
                </Button>
              ) : (
                <Button key={action.label} size="sm" onClick={action.onClick}>
                  {action.label}
                </Button>
              ),
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
